import { router } from "@inertiajs/react"
import {
  Award,
  type LucideIcon,
  MessageCircle,
  PartyPopper,
  Target,
  TrendingUp,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { formatRelativeTime } from "@/lib/relative-time"
import { cn } from "@/lib/utils"
import { notificationPath } from "@/routes"
import type {
  NotificationRow as NotificationRowType,
  NotificationType,
} from "@/types/notifications"

/**
 * OKS-398 — per-type icon badge config. Each `type` (server-derived in
 * `Notifications::Serializer`) maps to a Lucide icon + an accent tint
 * (badge background + icon color, light + dark). The kudo row also gets
 * a faint info-blue background tint (see `rowToneClass`). Color is never
 * the sole differentiator — the badge carries a localized `aria-label`
 * (`notifications.type.<type>`) and a distinct icon shape (UX Spec §6).
 */
const NOTIFICATION_TYPE_CONFIG: Record<
  NotificationType,
  { icon: LucideIcon; badgeClass: string }
> = {
  mission: {
    icon: Target,
    badgeClass: "bg-muted text-muted-foreground",
  },
  kudo: {
    icon: PartyPopper,
    badgeClass: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400",
  },
  level_up: {
    icon: TrendingUp,
    badgeClass:
      "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",
  },
  badge: {
    icon: Award,
    badgeClass:
      "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400",
  },
  message: {
    icon: MessageCircle,
    badgeClass: "bg-primary/10 text-primary",
  },
}

interface NotificationRowProps {
  notification: NotificationRowType
  /**
   * Density variant. `panel` is the compact bell-popover/sheet row;
   * `page` is the more spacious full-history list row (no body
   * truncation, more vertical padding) per UX Spec §2.
   */
  variant?: "panel" | "page"
  /** Called after the optimistic state flips read; used by the panel
   * to fade-out the unread accent before navigation. */
  onMarkRead?: () => void
}

/**
 * OKS-36 — Single notification row.
 *
 * Click semantics (UX Spec §1 happy path):
 *   1. Optimistic — local "read" state flips immediately.
 *   2. PATCH `/notifications/:id` — backend marks read (idempotent)
 *      then redirects to `target_url` when present, or `redirect_back
 *      fallback_location: notifications_path` otherwise (TRD §7).
 *
 * The FE never navigates manually; trusting the controller redirect
 * keeps a single source of truth for routing the missing-target_url
 * case (covers events whose route surface ships in a later ticket,
 * e.g. OKS-34's `creator.message.received`).
 *
 * Accessibility:
 *   - Native `<button>` so keyboard activation (Enter/Space) flows
 *     through the same handler as click.
 *   - `<time datetime="...">` so AT can announce the timestamp; the
 *     visible label is a relative-time string from i18n.
 *   - Unread state is indicated by BOTH the left dot AND the row
 *     background — color is not the sole indicator (UX Spec §6).
 *   - The typed icon badge (OKS-398) carries a localized `aria-label`
 *     so the type is conveyed to AT, not by color/icon alone.
 */
export function NotificationRow({
  notification,
  variant = "panel",
  onMarkRead,
}: NotificationRowProps) {
  const { t, i18n } = useTranslation()
  const [optimisticRead, setOptimisticRead] = useState(
    notification.read_at !== null,
  )

  const isUnread = !optimisticRead
  const { icon: TypeIcon, badgeClass } =
    NOTIFICATION_TYPE_CONFIG[notification.type]
  const isKudo = notification.type === "kudo"

  // Row background tone. Kudo gets a faint info-blue tint behind the
  // unread/read state (UX Spec §2); all other types keep the neutral
  // unread accent. Tailwind backgrounds don't layer, so the kudo tint
  // and the unread accent are composed into one class per state.
  const rowToneClass = isKudo
    ? isUnread
      ? "bg-sky-50 dark:bg-sky-500/10"
      : "bg-sky-50/50 hover:bg-sky-100/40 dark:bg-sky-500/[0.06]"
    : isUnread
      ? "bg-primary/5"
      : "hover:bg-muted/40 bg-transparent"

  function handleClick() {
    if (isUnread) {
      setOptimisticRead(true)
      onMarkRead?.()
    }

    router.patch(
      notificationPath(notification.id),
      {},
      {
        preserveScroll: false,
        onError: () => {
          // PATCH failed — flip the optimistic state back so the row
          // remains visibly unread. The next poll/visit will re-sync.
          setOptimisticRead(notification.read_at !== null)
        },
      },
    )
  }

  return (
    <button
      type="button"
      data-testid={`notification-row-${notification.id}`}
      data-state={isUnread ? "unread" : "read"}
      onClick={handleClick}
      className={cn(
        "group relative flex w-full items-start gap-3 border-b text-left transition-colors outline-none last:border-b-0",
        "focus-visible:bg-muted/60",
        variant === "panel" ? "px-4 py-3" : "px-4 py-4 sm:px-6 sm:py-5",
        rowToneClass,
      )}
    >
      {isUnread && (
        <span
          aria-hidden="true"
          data-testid={`notification-row-${notification.id}-unread-dot`}
          className="bg-primary absolute top-1/2 left-1.5 size-1.5 -translate-y-1/2 rounded-full"
        />
      )}

      <span
        data-testid={`notification-row-${notification.id}-icon`}
        data-notification-type={notification.type}
        aria-label={t(`notifications.type.${notification.type}`)}
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full",
          badgeClass,
        )}
      >
        <TypeIcon aria-hidden="true" className="size-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p
          className={cn(
            "text-sm leading-tight",
            isUnread ? "font-semibold" : "font-medium",
            variant === "panel" ? "truncate" : "line-clamp-2",
          )}
        >
          {notification.title}
        </p>
        <p
          className={cn(
            "text-muted-foreground text-xs",
            variant === "panel" ? "truncate" : "",
          )}
        >
          {notification.body}
        </p>
        <time
          dateTime={notification.created_at}
          className="text-muted-foreground/80 mt-1 text-[11px] tracking-wide uppercase"
        >
          {formatRelativeTime(notification.created_at, t, i18n.language)}
        </time>
      </div>
    </button>
  )
}
